/**
 * Campaign hotel+flight order financial overlay — aligns payment UI with
 * admin_campaign_hotel.pricing (same SSOT as Price TRACE).
 */
import {
  isCampaignOrderShape,
  resolveCampaignPricingForTrace,
} from "../../Components/Orders/orderDetailsModal/ldsOrderPriceTraceSsot.js";
import { resolveLdsConfirmedPaidSumFromPayments } from "./ldsOrderPaymentSummarySsot.js";
import {
  coerceCampaignClientsInfo,
  isCampaignOrderAttributedToAgent,
} from "./campaignOrderContext.js";

const MONEY_TOL = 0.02;

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickPositive(...values) {
  for (const value of values) {
    const amount = toNumberOrNull(value);
    if (amount != null && amount > MONEY_TOL) return round2(amount);
  }
  return null;
}

function truthyPaidFlag(raw) {
  return raw === true || raw === "true" || raw === 1 || raw === "1";
}

function isCampaignOrderMarkedPaid(order) {
  if (!order || typeof order !== "object") return false;
  if (truthyPaidFlag(order.paid)) return true;

  const ch =
    order.campaign_hotel && typeof order.campaign_hotel === "object"
      ? order.campaign_hotel
      : null;
  const admin =
    order.admin_campaign_hotel && typeof order.admin_campaign_hotel === "object"
      ? order.admin_campaign_hotel
      : null;

  if (truthyPaidFlag(ch?.paid) || truthyPaidFlag(admin?.paid)) return true;

  const status = String(
    admin?.payment_status ?? ch?.payment_status ?? order.payment_status ?? ""
  )
    .trim()
    .toLowerCase();
  if (!status) return false;
  if (status === "paid" || status === "fully_paid" || status.includes("שולם במלואו")) {
    return true;
  }
  return false;
}

/**
 * Resolve campaign customer total (charged) from pricing TRACE SSOT.
 * @returns {{ total: number|null, totalSource: string, pricing: Record<string, unknown>|null, traceRoot: string|null }}
 */
export function resolveCampaignOrderCustomerTotal(order, options = {}) {
  if (!isCampaignOrderShape(order)) {
    return { total: null, totalSource: "none", pricing: null, traceRoot: null };
  }

  const campaignTrace = resolveCampaignPricingForTrace(order, options);
  const pricing =
    campaignTrace?.pricing && typeof campaignTrace.pricing === "object"
      ? campaignTrace.pricing
      : null;
  if (!pricing) {
    return { total: null, totalSource: "none", pricing: null, traceRoot: null };
  }

  const traceRoot = campaignTrace.traceSourceRoot || "admin_campaign_hotel.pricing";
  const total = pickPositive(
    pricing.charged_ils,
    pricing.customer_total_ils,
    pricing.final_sale_price_ils
  );

  if (total == null) {
    return { total: null, totalSource: "none", pricing, traceRoot };
  }

  let totalSource = `${traceRoot}.charged_ils`;
  if (pricing.charged_ils == null || toNumberOrNull(pricing.charged_ils) == null) {
    if (pricing.customer_total_ils != null) {
      totalSource = `${traceRoot}.customer_total_ils`;
    } else {
      totalSource = `${traceRoot}.final_sale_price_ils`;
    }
  }

  return { total, totalSource, pricing, traceRoot };
}

/**
 * Overlay for LDS backend financial blocks when campaign pricing is authoritative.
 * @returns {null|{
 *   total: number|null,
 *   totalSource: string,
 *   paid: number|null,
 *   paidSource: string,
 *   remaining: number|null,
 *   remainingSource: string,
 * }}
 */
export function resolveCampaignOrderFinancialOverlay(order, options = {}) {
  const { total, totalSource, pricing, traceRoot } = resolveCampaignOrderCustomerTotal(
    order,
    options
  );
  if (total == null || !pricing) return null;

  const paymentsPaid = resolveLdsConfirmedPaidSumFromPayments(order);
  const paymentsSum = paymentsPaid.value ?? 0;

  let paid = null;
  let paidSource = "none";

  if (paymentsSum > MONEY_TOL) {
    paid = round2(Math.min(paymentsSum, total));
    paidSource = paymentsPaid.source;
  } else if (isCampaignOrderMarkedPaid(order)) {
    paid = total;
    paidSource = `${traceRoot}.charged_ils:order_marked_paid`;
  }

  if (paid == null) {
    return {
      total,
      totalSource,
      paid: 0,
      paidSource: "campaign:unpaid",
      remaining: total,
      remainingSource: "derived:campaign_total_minus_zero_paid",
    };
  }

  const remaining = round2(Math.max(total - paid, 0));
  return {
    total,
    totalSource,
    paid,
    paidSource,
    remaining,
    remainingSource:
      remaining <= MONEY_TOL
        ? "derived:campaign_fully_paid"
        : "derived:campaign_total_minus_paid",
  };
}

function pickNonNegativeIls(pricing, clientsInfo, ...keys) {
  for (const key of keys) {
    for (const src of [pricing, clientsInfo]) {
      const amount = toNumberOrNull(src?.[key]);
      if (amount != null && amount > 0) return round2(amount);
    }
  }
  return 0;
}

/**
 * Built-in public-channel agent commission vs manually added commission.
 * Does not change customer total. Wallet/UI entitlement is the sum when the
 * order is attributed to an agent.
 *
 * @returns {{
 *   builtInAgentAmount: number,
 *   addedAgentAmount: number,
 *   agentAmount: number,
 *   attributedToAgent: boolean,
 *   sourceRoot: string,
 * }|null}
 */
export function resolveCampaignOrderAgentCommissionParts(order, options = {}) {
  const { pricing, traceRoot } = resolveCampaignOrderCustomerTotal(order, options);
  if (!pricing || !traceRoot) return null;

  const ci = coerceCampaignClientsInfo(order);
  const builtInRaw = pickNonNegativeIls(
    pricing,
    ci,
    "flight_agent_commission_ils",
  );
  const addedRaw = pickNonNegativeIls(
    pricing,
    ci,
    "agent_added_commission_ils",
  );
  const attributedToAgent = isCampaignOrderAttributedToAgent(order);
  const builtInAgentAmount = attributedToAgent ? builtInRaw : 0;
  const addedAgentAmount = addedRaw;
  return {
    builtInAgentAmount,
    addedAgentAmount,
    agentAmount: round2(builtInAgentAmount + addedAgentAmount),
    attributedToAgent,
    sourceRoot: traceRoot,
  };
}

/**
 * Admin commercial breakdown rows for campaign orders (HR + agent commission).
 * `agentAmount` is total agent entitlement (built-in public + manually added).
 * @returns {{
 *   hrAmount: number,
 *   agentAmount: number,
 *   builtInAgentAmount: number,
 *   addedAgentAmount: number,
 *   sourceRoot: string,
 * }|null}
 */
export function resolveCampaignOrderCommercialBreakdown(order, options = {}) {
  const { pricing, traceRoot } = resolveCampaignOrderCustomerTotal(order, options);
  if (!pricing || !traceRoot) return null;

  const hrFromTotal = toNumberOrNull(pricing.hr_margin_total_ils);
  const hrFromParts =
    (toNumberOrNull(pricing.hotel_markup_ils) ?? 0) +
    (toNumberOrNull(pricing.flight_hidden_markup_ils) ?? 0);
  const hrAmount =
    hrFromTotal != null && hrFromTotal >= 0
      ? round2(hrFromTotal)
      : hrFromParts > 0
        ? round2(hrFromParts)
        : 0;

  const agentParts = resolveCampaignOrderAgentCommissionParts(order, options);

  return {
    hrAmount,
    agentAmount: agentParts?.agentAmount ?? 0,
    builtInAgentAmount: agentParts?.builtInAgentAmount ?? 0,
    addedAgentAmount: agentParts?.addedAgentAmount ?? 0,
    sourceRoot: traceRoot,
  };
}
